import React, { useState, useCallback } from 'react';
import { Search, Sparkles, RotateCcw, Loader2 } from 'lucide-react';
import { useMoleculeStore, useUIStore } from '../../store';
import { EXAMPLE_MOLECULES } from '../../utils/exampleMolecules';
import { parseSMILES } from '../../utils/chemistry';

interface MoleculeInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  showExamples?: boolean;
  className?: string;
}

export function MoleculeInput({
  value,
  onChange,
  onSubmit,
  placeholder = 'Enter SMILES, InChI, or molecule name...',
  label = 'Molecule Input',
  error,
  disabled,
  showExamples = true,
  className = ''
}: MoleculeInputProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  
  const { setCurrentMolecule } = useMoleculeStore();
  const { setLoading, showToast } = useUIStore();

  const handleSubmit = useCallback(async () => {
    if (!value.trim() || disabled || isGenerating) return;

    setIsGenerating(true);
    setInputError(null);
    setLoading(true, 'Generating molecule structure...');

    try {
      // First check if it's a pre-computed example molecule
      const example = EXAMPLE_MOLECULES.find(
        m => m.name.toLowerCase() === value.toLowerCase() ||
             m.smiles.toLowerCase() === value.toLowerCase() ||
             m.id === value.toLowerCase()
      );

      let molecule;
      
      if (example && example.molecule) {
        // Use pre-computed molecule for instant loading
        molecule = { ...example.molecule };
      } else {
        // Parse SMILES for non-precomputed molecules
        molecule = await parseSMILES(value);
      }

      if (molecule) {
        setCurrentMolecule(molecule);
        showToast({
          id: Date.now().toString(),
          type: 'success',
          title: 'Molecule Loaded',
          message: `Successfully loaded ${molecule.name || 'molecule'}`,
        });
        onSubmit?.();
      } else {
        setInputError('Could not parse molecule structure');
      }
    } catch (err) {
      setInputError(err instanceof Error ? err.message : 'Failed to generate molecule');
    } finally {
      setIsGenerating(false);
      setLoading(false);
    }
  }, [value, disabled, isGenerating, onChange, onSubmit, setCurrentMolecule, setLoading, showToast]);

  const handleExampleSelect = useCallback((example: typeof EXAMPLE_MOLECULES[0]) => {
    onChange(example.smiles);
    
    // If pre-computed, load immediately without parsing
    if (example.molecule) {
      setLoading(true, 'Loading molecule...');
      try {
        const molecule = { ...example.molecule };
        setCurrentMolecule(molecule);
        showToast({
          id: Date.now().toString(),
          type: 'success',
          title: 'Molecule Loaded',
          message: `Successfully loaded ${example.name}`,
        });
      } catch (err) {
        showToast({
          id: Date.now().toString(),
          type: 'error',
          title: 'Error',
          message: 'Failed to load molecule',
        });
      } finally {
        setLoading(false);
      }
    }
  }, [onChange, setCurrentMolecule, setLoading, showToast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={`input-section ${className}`}>
      {label && <label className="input-section-title">{label}</label>}
      
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setInputError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isGenerating}
          className={`input molecule-input-large ${inputError || error ? 'error' : ''}`}
          style={{ fontFamily: 'var(--font-mono)' }}
        />
        
        {isGenerating && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-400" />
          </div>
        )}
        
        {!isGenerating && value && (
          <button
            onClick={() => onChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-light"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </div>

      {(inputError || error) && (
        <p className="text-error text-sm" style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
          {inputError || error}
        </p>
      )}

      <div className="input-actions">
        <button
          onClick={handleSubmit}
          disabled={!value.trim() || disabled || isGenerating}
          className="btn btn-primary"
          style={{ flex: 1 }}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generate Structure
            </>
          )}
        </button>
      </div>

      {showExamples && (
        <div style={{ marginTop: '1.5rem' }}>
          <label className="input-section-title">Example Molecules</label>
          <div className="example-grid" style={{ marginTop: '0.75rem' }}>
            {EXAMPLE_MOLECULES.slice(0, 6).map((example) => (
              <button
                key={example.id}
                onClick={() => handleExampleSelect(example)}
                className="example-card"
                disabled={isGenerating}
              >
                <span className="example-name">{example.name}</span>
                <span className="example-formula">{example.formula}</span>
                <span className="example-category">{example.category}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MoleculeSearch({
  onSelect,
  placeholder = 'Search molecules...'
}: {
  onSelect: (molecule: { smiles: string; name: string }) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = query
    ? EXAMPLE_MOLECULES.filter(
        m => m.name.toLowerCase().includes(query.toLowerCase()) ||
             m.smiles.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5)
    : [];

  return (
    <div style={{ position: 'relative' }}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder={placeholder}
          className="input"
          style={{ paddingLeft: '2.5rem', width: '280px' }}
        />
      </div>

      {isOpen && query && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '0.5rem',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
          }}
        >
          {filtered.map((example) => (
            <button
              key={example.id}
              onClick={() => {
                onSelect({ smiles: example.smiles, name: example.name });
                setQuery('');
                setIsOpen(false);
              }}
              className="example-card"
              style={{
                width: '100%',
                borderRadius: 0,
                border: 'none',
                borderBottom: '1px solid var(--color-border)',
                textAlign: 'left',
              }}
            >
              <span className="example-name">{example.name}</span>
              <span className="example-formula">{example.formula}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

